import './styles.css';
import './lib/i18n';
import { ToastProvider } from '@runnerboy/ui';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router';
import { registerSW } from 'virtual:pwa-register';
import { LocationSheet, LoginSheet } from './components/sheets';
import { BottomNav, CartBar, OfflineBanner, ScrollToTop, TopBar } from './components/shell';
import { initNativeShell, isNative } from './lib/native';
import { SessionProvider, useSession } from './lib/session';
import { AccountPage, LegalPage, SupportPage } from './pages/account';
import { CartPage, CheckoutPage } from './pages/checkout';
import { HomePage } from './pages/home';
import { isOnboarded, Onboarding } from './pages/onboarding';
import { RoadSafetyPage } from './pages/safety';
import { SearchPage } from './pages/search';
import { OrderPage, OrdersPage, PayPage, ReceiptPage } from './pages/order';
import { StorePage } from './pages/store';

/** Information pages open directly from a link (road safety, privacy, terms): no age onboarding, no location prompt. */
const INFO_ROUTES = ['/road-safety', '/legal', '/privacy', '/terms'];
const TITLES: [RegExp, string][] = [
  [/^\/road-safety/, 'Road safety'],
  [/^\/(legal|privacy|terms)/, 'Privacy & terms'],
  [/^\/store\//, 'Store'],
  [/^\/search/, 'Search'],
  [/^\/cart/, 'Cart'],
  [/^\/checkout/, 'Checkout'],
  [/^\/pay\//, 'Payment'],
  [/^\/orders\/[^/]+/, 'Order'],
  [/^\/orders/, 'Orders'],
  [/^\/account/, 'Account'],
  [/^\/support/, 'Help'],
];

function App() {
  const { ready, location } = useSession();
  const { pathname } = useLocation();
  const info = INFO_ROUTES.some((r) => pathname.startsWith(r));
  const [locOpen, setLocOpen] = useState(false);
  const [onboarded, setOnboarded] = useState(isOnboarded);
  useEffect(() => {
    if (ready && onboarded && !location && !info) setLocOpen(true);
  }, [ready, onboarded, location, info]);
  useEffect(() => {
    const t = TITLES.find(([re]) => re.test(pathname))?.[1];
    document.title = t ? `${t} · Runner Boy` : 'Runner Boy · Don’t drink & drive. Order from your place.';
  }, [pathname]);
  useEffect(() => {
    void initNativeShell(() => false);
  }, []);
  return (
    <>
      <ScrollToTop />
      <OfflineBanner />
      <TopBar onLocation={() => setLocOpen(true)} />
      <Routes>
        <Route path="/" element={<HomePage onChooseLocation={() => setLocOpen(true)} />} />
        <Route path="/store/:storeId" element={<StorePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/road-safety" element={<RoadSafetyPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/pay/:orderId" element={<PayPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/orders/:orderId" element={<OrderPage />} />
        <Route path="/orders/:orderId/receipt" element={<ReceiptPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/privacy" element={<LegalPage />} />
        <Route path="/terms" element={<LegalPage />} />
        <Route path="*" element={<HomePage onChooseLocation={() => setLocOpen(true)} />} />
      </Routes>
      <CartBar />
      <BottomNav />
      <LoginSheet />
      <LocationSheet open={locOpen} onClose={() => setLocOpen(false)} />
      {!onboarded && !info && <Onboarding onDone={() => setOnboarded(true)} />}
    </>
  );
}

// The native app ships its assets inside the APK — no service worker there (it would only risk stale bundles).
if (!isNative) registerSW({ immediate: true });

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <ToastProvider>
      <SessionProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </SessionProvider>
    </ToastProvider>
  </StrictMode>,
);
